import { useEventListener } from 'usehooks-ts';
import MainRouter from "./MainRouter";
import { useExpireAuthData } from '../hooks/auth';
import { Header } from './Header';
import { PlayerlistSidebar } from './PlayerlistSidebar/PlayerlistSidebar';
import MainSheets from './MainSheets';
import WarningBar from './WarningBar';
import ConfirmDialog from '@/components/ConfirmDialog';
import PromptDialog from '@/components/PromptDialog';
import TxToaster from '@/components/TxToaster';
import AccountDialog from './AccountDialog';
import { useOpenAccountModal } from '@/hooks/dialogs';
import PlayerModal from './PlayerModal/PlayerModal';
import { playerModalUrlParam, useOpenPlayerModal } from '@/hooks/playerModal';
import { navigate as setLocation } from 'wouter/use-browser-location';
import MainSocket from './MainSocket';
import { TooltipProvider } from '@/components/ui/tooltip';
import { hotkeyEventListener } from '@/lib/hotkeyEventListener';
import ActionModal from './ActionModal/ActionModal';
import { useEffect, useRef } from 'react';
import { actionModalUrlParam, useOpenActionModal } from '@/hooks/actionModal';
import FcNavigation from './FcNavigation';
import { FcAdminStatusProvider } from '@/hooks/FcAdminStatusProvider';
import { PlayerDossierProvider } from '@/hooks/PlayerDossierProvider';
import { ShieldCheckIcon } from 'lucide-react';
import { cn } from '@/lib/utils';


export default function MainShell() {
    const expireSession = useExpireAuthData();
    const openAccountModal = useOpenAccountModal();
    const openPlayerModal = useOpenPlayerModal();
    const openActionModal = useOpenActionModal();
    const commandPanelRef = useRef<HTMLDivElement>(null);
    const isWebInterface = window.txConsts.isWebInterface;

    useEventListener('message', (e: TxMessageEvent) => {
        if (e.data.type === 'logoutNotice') {
            expireSession('child iframe', 'got logoutNotice');
        } else if (e.data.type === 'openAccountModal') {
            openAccountModal();
        } else if (e.data.type === 'openPlayerModal') {
            openPlayerModal(e.data.ref);
        } else if (e.data.type === 'navigateToPage') {
            setLocation(e.data.href);
        }
    });

    useEffect(() => {
        const pageUrl = new URL(window.location.toString());
        const playerModalRef = pageUrl.searchParams.get(playerModalUrlParam);
        const actionModalRef = pageUrl.searchParams.get(actionModalUrlParam);
        if (!playerModalRef && !actionModalRef) return;

        if (playerModalRef) {
            if (playerModalRef.includes('#')) {
                const [mutex, rawNetid] = playerModalRef.split('#');
                const netid = parseInt(rawNetid);
                if (mutex.length && rawNetid.length && !isNaN(netid)) {
                    return openPlayerModal({ mutex, netid });
                }
            } else if (playerModalRef.length) {
                return openPlayerModal({ license: playerModalRef });
            }
        } else if (actionModalRef && actionModalRef.length) {
            return openActionModal(actionModalRef);
        }

        pageUrl.searchParams.delete(playerModalUrlParam);
        pageUrl.searchParams.delete(actionModalUrlParam);
        window.history.replaceState({}, '', pageUrl);
    }, []);

    useEffect(() => {
        document.documentElement.classList.add('dark');
        document.documentElement.classList.remove('light');
        const element = commandPanelRef.current;
        if (!element) return;
        const observer = new ResizeObserver(([entry]) => {
            const width = Math.round(entry.contentRect.width);
            element.style.setProperty('--fc-panel-width', `${width}px`);
            element.dataset.contentSize = width < 640 ? 'narrow' : width < 960 ? 'compact' : 'wide';
        });
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    useEventListener('keydown', hotkeyEventListener);

    return <FcAdminStatusProvider><PlayerDossierProvider>
        <TooltipProvider delayDuration={300} disableHoverableContent={true}>
            <div className={cn('fc-shell dark', !isWebInterface && 'fc-shell--embedded')}>
                <div className="fc-panel">
                    <span className="fc-panel-accent" />
                    <span className="fc-panel-stripes" />
                    <span className="fc-panel-stripes flip" />
                    <ShieldCheckIcon className="fc-panel-watermark" strokeWidth={1.2} aria-hidden="true" />
                    <Header />
                    <div className="fc-shell-body">
                        <FcNavigation />
                        <main className="fc-shell-main">
                            <div className="fc-command-panel" ref={commandPanelRef}>
                                <div className="fc-command-content"><MainRouter /></div>
                            </div>
                        </main>
                        {isWebInterface && <PlayerlistSidebar />}
                    </div>
                </div>
            </div>

            <MainSheets />
            <WarningBar />
            <ConfirmDialog />
            <PromptDialog />
            <TxToaster />
            <AccountDialog />
            <PlayerModal />
            <ActionModal />
            <MainSocket />
        </TooltipProvider>
    </PlayerDossierProvider></FcAdminStatusProvider>;
}
